import type { SectionDef, TabId } from "../inspector/schema";
import { viewerSections } from "./viewer";
import { pickingSections } from "./picking";
import { sceneSections } from "./scene";
import { antialiasingSections } from "./antialiasing";
import { postfxSections } from "./postfx";
import { exportSections } from "./exportTab";
import { animationsSections } from "./animations";
import { modifiersSections } from "./modifiers";
import { configuratorsSections } from "./configurators";
import { pluginsSections } from "./plugins";
import { EXPERIMENTAL, GROUPS, PLAIN_TITLES } from "./groups";

/**
 * Every section, from the parts modules (which keep iJewel's order — the
 * layout guard reads them). Section ids are globally unique.
 */
export const ALL_SECTIONS: SectionDef[] = [
  ...viewerSections,
  ...pickingSections,
  ...sceneSections,
  ...antialiasingSections,
  ...postfxSections,
  ...exportSections,
  ...animationsSections,
  ...modifiersSections,
  ...configuratorsSections,
  ...pluginsSections,
];

const BY_ID = new Map(ALL_SECTIONS.map((s) => [s.id, s]));

/**
 * The section as Advanced SHOWS it: the plain title (iJewel's stays in
 * `title`, where verify:parity matches it) and its experimental flag.
 */
function present(s: SectionDef): SectionDef {
  return {
    ...s,
    displayTitle: PLAIN_TITLES[s.id] ?? s.displayTitle,
    experimental: EXPERIMENTAL[s.id],
  };
}

/** Group → sections registry (see groups.ts for why it is regrouped). */
export const TAB_SECTIONS = Object.fromEntries(
  GROUPS.map((g) => [g.id, g.sections.map((id) => BY_ID.get(id)).filter((s): s is SectionDef => !!s).map(present)]),
) as Record<TabId, SectionDef[]>;

export function tabSections(tab: TabId): SectionDef[] {
  return TAB_SECTIONS[tab] ?? [];
}

/** Which group a section lives in. */
export function groupOf(sectionId: string): TabId | null {
  for (const g of GROUPS) if (g.sections.includes(sectionId)) return g.id;
  return null;
}

/** Every group with its sections, in rail order — what search walks. */
export const ALL_GROUP_SECTIONS: { tabId: TabId; sections: SectionDef[] }[] = GROUPS.map((g) => ({
  tabId: g.id,
  sections: TAB_SECTIONS[g.id],
}));
